import React from 'react';
import styled from 'styled-components';
import { FaGithub, FaLinkedin, FaEnvelope } from 'react-icons/fa';

const HomeContainer = styled.section`
  min-height: 100vh;
  display: flex;
  align-items: center;
  padding: 0 5%;
  background: ${({ theme }) => theme.colors?.background || '#f5f7fa'};
  color: ${({ theme }) => theme.colors?.text || '#1f2937'};
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 60%;
    height: 100%;
    background: linear-gradient(
      to bottom right,
      rgba(37, 99, 235, 0.1) 0%,
      rgba(30, 64, 175, 0.2) 100%
    );
    clip-path: polygon(25% 0%, 100% 0%, 75% 100%, 0% 100%);
    z-index: 1;
    
    @media (max-width: 768px) {
      width: 100%;
      clip-path: none;
      opacity: 0.3;
    }
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    padding: 80px 1rem 2rem;
    min-height: auto;
  }
`;

const LeftSection = styled.div`
  flex: 1;
  padding-right: 2rem;
  position: relative;
  z-index: 2;
  
  @media (max-width: 768px) {
    padding-right: 0;
    margin-bottom: 2rem;
  }
`;

const RightSection = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  z-index: 2;
`;

const HelloText = styled.p`
  font-size: 1.5rem;
  color: ${({ theme }) => theme.colors?.primary || '#2563eb'};
  margin-bottom: 1rem;
  font-weight: 500;
`;

const Name = styled.h1`
  font-size: 3.5rem;
  margin: 0 0 1rem 0;
  color: ${({ theme }) => theme.colors?.heading || '#1e293b'};
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Title = styled.h2`
  font-size: 1.8rem;
  margin: 0 0 1.5rem 0;
  color: ${({ theme }) => theme.colors?.primary || '#2563eb'};
  font-weight: 500;
  line-height: 1.3;
  
  @media (max-width: 768px) {
    font-size: 1.4rem;
    padding: 0 1rem;
    margin-bottom: 1.2rem;
  }
`;

const Highlight = styled.span`
  color: ${({ theme }) => theme.colors?.primary || '#2563eb'};
  font-weight: 600;
`;

const Description = styled.p`
  font-size: 1.1rem;
  line-height: 1.8;
  margin-bottom: 2rem;
  color: ${({ theme }) => theme.colors?.text || '#1f2937'};
  max-width: 100%;
  word-wrap: break-word;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    line-height: 1.6;
    padding: 0 1rem;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  
  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const Button = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.8rem 1.8rem;
  border-radius: 4px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  font-size: 1rem;
  cursor: pointer;
`;

const PrimaryButton = styled(Button)`
  background: ${({ theme }) => theme.colors?.primary || '#2563eb'};
  color: white;
  border: 2px solid ${({ theme }) => theme.colors?.primary || '#2563eb'};
  
  &:hover {
    background: transparent;
    color: ${({ theme }) => theme.colors?.primary || '#2563eb'};
  }
`;

const SecondaryButton = styled(Button)`
  background: transparent;
  color: ${({ theme }) => theme.colors?.primary || '#2563eb'};
  border: 2px solid ${({ theme }) => theme.colors?.primary || '#2563eb'};
  
  &:hover {
    background: ${({ theme }) => theme.colors?.primary || '#2563eb'};
    color: white;
  }
`;

const SocialIcons = styled.div`
  display: flex;
  gap: 1.5rem;
  margin-top: 1.5rem;
  flex-wrap: wrap;
  justify-content: center;
  
  @media (max-width: 480px) {
    gap: 1rem;
    padding: 0 1rem;
  }
`;

const SocialIcon = styled.a`
  color: ${({ theme }) => theme.colors?.text || '#1f2937'};
  font-size: 1.5rem;
  transition: color 0.3s ease;
  white-space: nowrap;
  
  &:hover {
    color: ${({ theme }) => theme.colors?.primary || '#2563eb'};
  }
`;

const ProfileImage = styled.img`
  width: 100%;
  max-width: 350px;
  height: auto;
  border-radius: 50%;
  border: 5px solid ${({ theme }) => theme.colors?.primary || '#2563eb'};
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  object-fit: cover;
  aspect-ratio: 1/1;
  
  @media (max-width: 768px) {
    max-width: 250px;
    margin: 0 auto;
  }
`;

const Home = () => {
  return (
    <HomeContainer id="home">
      <LeftSection>
        <HelloText>Hello, I'm</HelloText>
        <Name>Akash Agarwal</Name>
        <Title>Data Engineer | Big Data Engineer | AI Enthusiast</Title>
        <Description>
          I solve real-life problems using my technical expertise in <Highlight>Big Data</Highlight>, <Highlight>AWS</Highlight>, <Highlight>Azure</Highlight> and <Highlight>GenAI</Highlight>.
          Currently working as a <Highlight>Lead Data Engineer at John Deere</Highlight>, specialized in data pipelines and cloud solutions.
        </Description>
        <ButtonContainer>
          <PrimaryButton href="#contact">Contact Me</PrimaryButton>
        </ButtonContainer>
        <SocialIcons>
          <SocialIcon href="https://github.com/Akash491" target="_blank" rel="noopener noreferrer">
            <FaGithub />
          </SocialIcon>
          <SocialIcon href="https://linkedin.com/in/agarwalakash491" target="_blank" rel="noopener noreferrer">
            <FaLinkedin />
          </SocialIcon>
          <SocialIcon href="mailto:agarwalakash491@gmail.com">
            <FaEnvelope />
          </SocialIcon>
        </SocialIcons>
      </LeftSection>
      <RightSection>
        <ProfileImage src={require("../../assets/images/Akash-Agarwal.png")} alt="Akash Agarwal" />
      </RightSection>
    </HomeContainer>
  );
};

export default Home;
